/**
 * Order Processing & Status Tracking Module
 * Manages order creation, lifecycle state transitions, and real-time order tracking.
 */

export const ORDER_STATUSES = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  PREPARING: "preparing",
  READY: "ready",
  COMPLETED: "completed",
  CANCELLED: "cancelled"
};

/** Distinguishes a self-placed order from a gift purchased for another client. */
export const ORDER_TYPES = {
  SELF: "self",
  GIFT: "gift",
};

export class OrderModule {
  constructor() {
    this.orders = [];
  }

  setOrders(orders) {
    this.orders = Array.isArray(orders) ? orders : [];
  }

  validateOrderData(order) {
    if (!order || typeof order !== "object") return { valid: false, message: "Invalid order object" };
    if (!order.clientId) return { valid: false, message: "Client ID is required" };
    if (!order.providerId) return { valid: false, message: "Provider ID is required" };
    if (!order.items || !Array.isArray(order.items) || order.items.length === 0) {
      return { valid: false, message: "Order must contain at least one item" };
    }
    if (typeof order.totalAmount !== "number" || order.totalAmount <= 0) {
      return { valid: false, message: "Total amount must be greater than zero" };
    }
    // Gift order validation: giftOrderId must be a non-empty string when orderType is 'gift'
    if (order.orderType === ORDER_TYPES.GIFT && !order.giftOrderId) {
      return { valid: false, message: "giftOrderId is required for gift orders" };
    }
    return { valid: true };
  }

  canTransitionStatus(currentStatus, nextStatus) {
    const validTransitions = {
      [ORDER_STATUSES.PENDING]: [ORDER_STATUSES.CONFIRMED, ORDER_STATUSES.CANCELLED],
      [ORDER_STATUSES.CONFIRMED]: [ORDER_STATUSES.PREPARING, ORDER_STATUSES.CANCELLED],
      [ORDER_STATUSES.PREPARING]: [ORDER_STATUSES.READY, ORDER_STATUSES.CANCELLED],
      [ORDER_STATUSES.READY]: [ORDER_STATUSES.COMPLETED],
      [ORDER_STATUSES.COMPLETED]: [],
      [ORDER_STATUSES.CANCELLED]: []
    };

    const allowed = validTransitions[currentStatus] || [];
    return allowed.includes(nextStatus);
  }

  canProcessInternalAlphaOrder(order, { providerApproved = false, allowSandbox = true } = {}) {
    const validation = this.validateOrderData(order);
    if (!validation.valid) {
      return { valid: false, reason: validation.message };
    }

    if (!providerApproved) {
      return {
        valid: false,
        reason: "Provider must be approved before an internal alpha order can be created."
      };
    }

    if (!allowSandbox) {
      return {
        valid: false,
        reason: "Only sandbox or internal-alpha orders can be processed at this stage."
      };
    }

    return { valid: true };
  }
}

export const orderModule = new OrderModule();
